/**
 * Looking up other players.
 *
 * Each user is a channel, so a name and rating stay current without the lobby having to
 * carry a copy of everyone. The important detail is that `useUser` is a hook and is
 * therefore called by a *component per row* — never inside a `.map` in a parent. The 2023
 * lobby called hooks inside a loop over challenges, which breaks the rule that hook order
 * must be stable and produces state that silently belongs to the wrong row when the list
 * reorders.
 */

import { reduceUser, userChannel, type UserState } from '@gok/shared';

import { useChannel } from './context.tsx';
import { Seat } from '../ui/primitives.tsx';

export function useUser(userId: string | undefined) {
  return useChannel<UserState, never>(
    userId ? userChannel(userId) : null,
    reduceUser,
  )
    .state;
}

export function UserName({
  userId,
  seat,
  className = '',
}: {
  userId: string;
  seat?: 0 | 1;
  className?: string;
}) {
  const user = useUser(userId);

  return (
    <span className={`flex min-w-0 items-center gap-1.5 ${className}`}>
      {seat !== undefined && <Seat player={seat} />}
      <span className='truncate font-medium'>{user?.username ?? '…'}</span>
      {user && (
        <span className='tabular shrink-0 font-mono text-[0.7rem] text-muted'>
          {Math.round(user.rating.mean)}
          {user.rating.rd > 100 && (
            <span title='Provisional — too few games to be confident yet'>
              ?
            </span>
          )}
        </span>
      )}
    </span>
  );
}
